import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore, useUserStore } from '../stores/movieStore';

const UserPage = () => {
  const { user, isAuthenticated, logout, updateUser } = useAuthStore();
  const { favorites, history, clearHistory } = useUserStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'profile' | 'favorites' | 'history'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    username: user?.username || '',
    email: user?.email || '',
  });

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">请先登录</h2>
          <Link to="/login" className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl">去登录</Link>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    toast.success('已退出登录');
    navigate('/');
  };

  const handleSaveProfile = () => {
    if (!editForm.username.trim()) {
      toast.error('用户名不能为空');
      return;
    }
    updateUser({ username: editForm.username });
    toast.success('资料更新成功');
    setIsEditing(false);
  };

  const tabs = [
    { id: 'profile', label: '个人资料', icon: '👤' },
    { id: 'favorites', label: '我的收藏', icon: '❤️', count: favorites.length },
    { id: 'history', label: '观看历史', icon: '📜', count: history.length },
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass rounded-2xl p-6 mb-8">
          <div className="flex items-center space-x-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold">
                {user?.username?.charAt(0).toUpperCase()}
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white mb-1">{user?.username}</h1>
              <p className="text-gray-400">{user?.email}</p>
              <div className="flex items-center space-x-4 mt-2">
                <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded-full">
                  {user?.role === 'admin' ? '管理员' : '普通用户'}
                </span>
              </div>
            </div>
            <div className="flex space-x-3">
              <button onClick={handleLogout} className="px-4 py-2 text-gray-400 hover:text-red-400 border border-slate-600 rounded-xl transition-colors">
                退出登录
              </button>
            </div>
          </div>
        </div>

        <div className="flex space-x-1 mb-8 bg-slate-800/50 p-1 rounded-xl">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        <div className="glass rounded-2xl p-6">
          {activeTab === 'profile' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">个人资料</h2>
                <button onClick={() => setIsEditing(!isEditing)} className="px-4 py-2 text-sm text-blue-400 hover:text-blue-300 transition-colors">
                  {isEditing ? '取消' : '编辑'}
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">用户名</label>
                  {isEditing ? (
                    <input type="text" value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} className="w-full px-4 py-3 bg-slate-800/50 border border-slate-600 rounded-xl text-white focus:border-blue-500 outline-none" />
                  ) : (
                    <p className="text-white">{user?.username}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">邮箱</label>
                  <p className="text-white">{user?.email}</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">账号类型</label>
                  <p className="text-white">{user?.role === 'admin' ? '管理员' : '普通用户'}</p>
                </div>
                {isEditing && (
                  <button onClick={handleSaveProfile} className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-xl">保存修改</button>
                )}
              </div>
            </div>
          )}

          {activeTab === 'favorites' && (
            <div>
              <h2 className="text-xl font-bold text-white mb-6">我的收藏</h2>
              {favorites.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-400 mb-4">还没有收藏任何影视作品</p>
                  <Link to="/" className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl">去发现</Link>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {favorites.map((item) => (
                    <Link key={`${item.source}:${item.videoId}`} to={`/movie/${item.videoId}`} className="group">
                      <div className="relative rounded-xl overflow-hidden">
                        <img src={item.poster} alt={item.title} className="w-full aspect-[2/3] object-cover group-hover:scale-110 transition-transform duration-300" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="absolute bottom-3 left-3 right-3">
                            <p className="text-white text-sm font-medium truncate">{item.title}</p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">观看历史</h2>
                {history.length > 0 && (
                  <button onClick={() => { if (confirm('确定要清空观看历史吗？')) { clearHistory(); toast.success('历史已清空'); } }} className="text-sm text-gray-400 hover:text-red-400 transition-colors">清空历史</button>
                )}
              </div>
              {history.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-400 mb-4">还没有观看记录</p>
                  <Link to="/" className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl">去观看</Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {history.map((item) => (
                    <Link key={`${item.source}:${item.videoId}`} to={`/play/${item.videoId}?source=${item.sourceId}`} className="flex items-center space-x-4 p-4 bg-slate-800/50 rounded-xl hover:bg-slate-700/50 transition-colors">
                      <img src={item.poster} alt={item.title} className="w-16 h-24 object-cover rounded-lg" />
                      <div className="flex-1">
                        <h3 className="text-white font-medium mb-1">{item.title}</h3>
                        <p className="text-gray-400 text-sm">{item.year} · {item.genre[0]}</p>
                        <div className="mt-2 flex items-center space-x-2">
                          <div className="flex-1 h-1 bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500" style={{ width: `${item.progress}%` }} />
                          </div>
                          <span className="text-xs text-gray-500">{item.progress}%</span>
                        </div>
                      </div>
                      <div className="text-gray-500 text-sm">{new Date(item.watchedAt).toLocaleDateString()}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPage;
